import type { BlockNode } from '../types';

export interface PagePreset {
  id: string;
  name: string;
  description: string;
  defaultTitle: string;
  defaultPath: string;
  category: 'general' | 'legal' | 'academics' | 'commerce';
  generateRoot: (pageId: string) => BlockNode;
}

export const PAGE_PRESETS: PagePreset[] = [
  {
    id: 'blank',
    name: 'Blank Page',
    description: 'Clean slate with an empty responsive container',
    defaultTitle: 'New Page',
    defaultPath: '/new-page',
    category: 'general',
    generateRoot: (pageId: string): BlockNode => ({
      id: `root-${pageId}`,
      type: 'Container',
      category: 'layout',
      props: { maxWidth: '1200px', centered: true },
      styles: { base: { padding: '24px 16px' } },
      children: [],
    }),
  },
  {
    id: 'toppers',
    name: 'Toppers & Hall of Fame',
    description: 'Showcase student ranks, national achievements, and testimonials',
    defaultTitle: 'Hall of Fame & Top Ranks',
    defaultPath: '/toppers',
    category: 'academics',
    generateRoot: (pageId: string): BlockNode => ({
      id: `root-${pageId}`,
      type: 'Container',
      category: 'layout',
      props: { maxWidth: '1200px', centered: true },
      styles: { base: { padding: '32px 16px' } },
      children: [
        {
          id: `toppers-showcase-${Date.now()}`,
          type: 'ToppersShowcase',
          category: 'academics',
          props: {
            headline: 'Our Star Performers & Toppers',
            subheadline: 'Celebrating the incredible hard work and national ranks of our dedicated students.',
            badgeText: 'Hall of Fame 2025',
          },
          styles: { base: {} },
          children: [
            {
              id: `topper-1-${Date.now()}`,
              type: 'TopperCard',
              category: 'academics',
              props: {
                studentName: 'Aarav Sharma',
                examName: 'JEE Advanced 2025',
                rank: 'AIR 42',
                score: 'Score: 318/360',
                photoUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&auto=format&fit=crop&q=80',
                testimonial: 'The rigorous test series and one-on-one doubt resolution made all the difference in my physics rank.',
                batchYear: '2-Year Classroom Batch',
              },
              styles: { base: {} },
            },
            {
              id: `topper-2-${Date.now()}`,
              type: 'TopperCard',
              category: 'academics',
              props: {
                studentName: 'Priya Patel',
                examName: 'NEET UG 2025',
                rank: 'AIR 18',
                score: 'Score: 712/720',
                photoUrl: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&auto=format&fit=crop&q=80',
                testimonial: 'Biology concept diagrams and chemistry revision notes were exceptionally structured.',
                batchYear: 'Classroom Batch 2025',
              },
              styles: { base: {} },
            },
            {
              id: `topper-3-${Date.now()}`,
              type: 'TopperCard',
              category: 'academics',
              props: {
                studentName: 'Rohan Deshmukh',
                examName: 'CBSE Class 12 Boards',
                rank: '99.4% Aggregate',
                score: 'PCM: 298/300',
                photoUrl: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&auto=format&fit=crop&q=80',
                testimonial: 'Helped me balance board exam presentations with competitive entrance preparation.',
                batchYear: 'Target 2025 Batch',
              },
              styles: { base: {} },
            },
          ],
        },
      ],
    }),
  },
  {
    id: 'digital-store',
    name: 'Digital Study Materials Store',
    description: 'Downloadable formula sheets, PDF study notes, and mock test papers',
    defaultTitle: 'Digital Study Materials & Downloads',
    defaultPath: '/digital-store',
    category: 'commerce',
    generateRoot: (pageId: string): BlockNode => ({
      id: `root-${pageId}`,
      type: 'Container',
      category: 'layout',
      props: { maxWidth: '1200px', centered: true },
      styles: { base: { padding: '32px 16px' } },
      children: [
        {
          id: `prod-list-${Date.now()}`,
          type: 'DigitalProductList',
          category: 'commerce',
          props: {
            sectionTitle: 'High-Yield Formula Sheets & Practice Kits',
          },
          styles: { base: {} },
          children: [
            {
              id: `prod-1-${Date.now()}`,
              type: 'DigitalProductCard',
              category: 'commerce',
              props: {
                title: 'JEE Physics Formula Handbook',
                description: 'Complete class 11 & 12 formulas, key derivations, and shortcut solving methods.',
                price: 199,
                currency: 'INR',
                fileType: 'PDF',
                badge: 'Bestseller',
                downloadUrl: '#download-physics',
                thumbnail: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=600&auto=format&fit=crop&q=80',
              },
              styles: { base: {} },
            },
            {
              id: `prod-2-${Date.now()}`,
              type: 'DigitalProductCard',
              category: 'commerce',
              props: {
                title: 'Organic Chemistry Reaction Maps',
                description: 'Full roadmap of conversions, name reactions, and mechanism summaries for rapid revision.',
                price: 249,
                currency: 'INR',
                fileType: 'PDF',
                badge: 'Updated 2026',
                downloadUrl: '#download-chemistry',
                thumbnail: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=600&auto=format&fit=crop&q=80',
              },
              styles: { base: {} },
            },
            {
              id: `prod-3-${Date.now()}`,
              type: 'DigitalProductCard',
              category: 'commerce',
              props: {
                title: 'Full Mock Test Series (10 Tests)',
                description: 'Timed online CBT tests with detailed question explanations and percentile ranking.',
                price: 499,
                currency: 'INR',
                fileType: 'Mock Test',
                badge: 'Instant Access',
                downloadUrl: '#download-mock',
                thumbnail: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&auto=format&fit=crop&q=80',
              },
              styles: { base: {} },
            },
          ],
        },
      ],
    }),
  },
  {
    id: 'terms',
    name: 'Terms & Conditions',
    description: 'Legal agreement covering enrollment, student code of conduct, and fees',
    defaultTitle: 'Terms & Conditions',
    defaultPath: '/terms-and-conditions',
    category: 'legal',
    generateRoot: (pageId: string): BlockNode => ({
      id: `root-${pageId}`,
      type: 'Container',
      category: 'layout',
      props: { maxWidth: '900px', centered: true },
      styles: { base: { padding: '40px 16px' } },
      children: [
        {
          id: `terms-h1-${Date.now()}`,
          type: 'Heading',
          category: 'typography',
          props: { text: 'Terms and Conditions', level: 'h1', align: 'left' },
          styles: { base: {} },
        },
        {
          id: `terms-date-${Date.now()}`,
          type: 'Paragraph',
          category: 'typography',
          props: { text: 'Last Updated: October 2026. Please read these terms carefully before enrolling in our programs or using our portal.', size: 'sm' },
          styles: { base: {} },
        },
        {
          id: `terms-div1-${Date.now()}`,
          type: 'Divider',
          category: 'layout',
          props: { thickness: '1px', style: 'solid' },
          styles: { base: {} },
        },
        {
          id: `terms-rich-${Date.now()}`,
          type: 'RichTextBlock',
          category: 'typography',
          props: {
            contentHTML: `
              <h3>1. Enrollment and Admission</h3>
              <p>Admission into classroom and online programs is subject to the submission of accurate student details, payment of prescribed tuition dues, and verification of eligibility criteria.</p>
              <h3>2. Academic Integrity and Attendance</h3>
              <p>Students are required to maintain regular attendance in scheduled batches and uphold academic honesty during periodic evaluations, mock examinations, and assessments.</p>
              <h3>3. Intellectual Property</h3>
              <p>All study material, practice sheets, recorded video sessions, and formula summaries provided by the institute are proprietary and strictly for individual enrolled student use. Unauthorized distribution is prohibited.</p>
              <h3>4. Fee Schedule and Cancellations</h3>
              <p>Fees once paid for enrolled academic sessions or digital study packs are subject to our institutional refund policy as specified during enrollment verification.</p>
            `,
          },
          styles: { base: {} },
        },
        {
          id: `terms-consent-${Date.now()}`,
          type: 'LegalAgreementBlock',
          category: 'legal',
          props: {
            title: 'Student Acknowledgment & Consent',
            agreementText: 'By enrolling or checking the consent box, the student and parent affirm agreement to these terms.',
            termsUrl: '/terms-and-conditions',
            privacyUrl: '/privacy-policy',
            cookieUrl: '/cookie-policy',
            requireMarketingConsent: true,
          },
          styles: { base: {} },
        },
      ],
    }),
  },
  {
    id: 'privacy',
    name: 'Privacy Policy',
    description: 'DPDPA & GDPR compliant student data collection, records, and security notice',
    defaultTitle: 'Privacy Policy',
    defaultPath: '/privacy-policy',
    category: 'legal',
    generateRoot: (pageId: string): BlockNode => ({
      id: `root-${pageId}`,
      type: 'Container',
      category: 'layout',
      props: { maxWidth: '900px', centered: true },
      styles: { base: { padding: '40px 16px' } },
      children: [
        {
          id: `priv-h1-${Date.now()}`,
          type: 'Heading',
          category: 'typography',
          props: { text: 'Privacy Policy', level: 'h1', align: 'left' },
          styles: { base: {} },
        },
        {
          id: `priv-date-${Date.now()}`,
          type: 'Paragraph',
          category: 'typography',
          props: { text: 'Your privacy is paramount. This policy outlines how student and parent information is gathered, managed, and safeguarded.', size: 'sm' },
          styles: { base: {} },
        },
        {
          id: `priv-div1-${Date.now()}`,
          type: 'Divider',
          category: 'layout',
          props: { thickness: '1px', style: 'solid' },
          styles: { base: {} },
        },
        {
          id: `priv-rich-${Date.now()}`,
          type: 'RichTextBlock',
          category: 'typography',
          props: {
            contentHTML: `
              <h3>1. Data We Collect</h3>
              <p>We collect essential student contact details (name, email, phone number, grade/class), academic records, attendance logs, and test performance data to administer coaching programs.</p>
              <h3>2. Purpose of Processing</h3>
              <p>Data is utilized strictly to provide educational services, issue hall tickets, publish rank cards, generate fee invoices, and communicate important circulars.</p>
              <h3>3. Security and Storage</h3>
              <p>Student records are stored within tenant-isolated databases with encrypted access control. We do not sell or monetize personal data to third parties.</p>
              <h3>4. Contact Data Protection Officer</h3>
              <p>For any inquiries regarding personal data records or erasure requests, contact our administration office at privacy@thesci.co.</p>
            `,
          },
          styles: { base: {} },
        },
        {
          id: `priv-consent-${Date.now()}`,
          type: 'ConsentCheckboxBlock',
          category: 'legal',
          props: {
            label: 'I understand and consent to the data practices described in this Privacy Policy',
            policyLink: '/privacy-policy',
            required: true,
          },
          styles: { base: {} },
        },
      ],
    }),
  },
  {
    id: 'cookies',
    name: 'Cookie Policy',
    description: 'Detailed statement of session tokens, analytics cookies, and consent controls',
    defaultTitle: 'Cookie Policy',
    defaultPath: '/cookie-policy',
    category: 'legal',
    generateRoot: (pageId: string): BlockNode => ({
      id: `root-${pageId}`,
      type: 'Container',
      category: 'layout',
      props: { maxWidth: '900px', centered: true },
      styles: { base: { padding: '40px 16px' } },
      children: [
        {
          id: `cookie-h1-${Date.now()}`,
          type: 'Heading',
          category: 'typography',
          props: { text: 'Cookie & Tracking Policy', level: 'h1', align: 'left' },
          styles: { base: {} },
        },
        {
          id: `cookie-date-${Date.now()}`,
          type: 'Paragraph',
          category: 'typography',
          props: { text: 'Learn how our portal uses cookies and local browser storage to keep you logged in and optimize performance.', size: 'sm' },
          styles: { base: {} },
        },
        {
          id: `cookie-div1-${Date.now()}`,
          type: 'Divider',
          category: 'layout',
          props: { thickness: '1px', style: 'solid' },
          styles: { base: {} },
        },
        {
          id: `cookie-rich-${Date.now()}`,
          type: 'RichTextBlock',
          category: 'typography',
          props: {
            contentHTML: `
              <h3>1. What are Cookies?</h3>
              <p>Cookies are small text identifiers placed on your browser when visiting web pages that help remember authentication sessions and device preferences.</p>
              <h3>2. Essential Cookies</h3>
              <p>Necessary cookies enable core portal navigation, login verification, CSRF protection, and cart state during online fee payments.</p>
              <h3>3. Managing Preferences</h3>
              <p>You can adjust or revoke your cookie choices at any time via your browser settings or using the banner below.</p>
            `,
          },
          styles: { base: {} },
        },
        {
          id: `cookie-banner-${Date.now()}`,
          type: 'CookieBannerBlock',
          category: 'legal',
          props: {
            noticeText: 'We use cookies to maintain your login session and measure website performance.',
            acceptButtonText: 'Accept All',
            declineButtonText: 'Decline Optional',
            cookiePolicyUrl: '/cookie-policy',
          },
          styles: { base: {} },
        },
      ],
    }),
  },
];
